import { useCallback } from 'react';

import type { Connection, EdgeChange, NodeChange } from '@xyflow/react';
import { addEdge } from '@xyflow/react';

import { emitRecipeModified } from '../events/recipeEditorEvents';

interface UseFlowChangeHandlersOptions {
  isReadOnly: boolean;
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (edgeChanges: EdgeChange[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ReactFlow edges array type
  setEdges: React.Dispatch<React.SetStateAction<any[]>>;
  onConnectFlow: (params: Connection) => void;
  debouncedUpdateRecipe: () => void;
}

/**
 * Custom hook that provides change handlers for ReactFlow
 * Handles node/edge changes and emits modification events
 */
export const useFlowChangeHandlers = ({
  isReadOnly,
  onNodesChange,
  onEdgesChange,
  setEdges,
  onConnectFlow,
  debouncedUpdateRecipe,
}: UseFlowChangeHandlersOptions) => {
  const handleOnNodesChange = useCallback(
    (changes: NodeChange[]) => {
      if (isReadOnly) return;

      // Check if there are non-position changes
      const hasNonPositionChanges = changes.some(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ReactFlow change type union
        (change: any) => change.type !== 'position' && change.type !== 'select'
      );

      // Apply changes to ReactFlow
      onNodesChange(changes);

      // Only mark as modified if there are actual content changes
      if (hasNonPositionChanges) {
        emitRecipeModified();
        debouncedUpdateRecipe();
      }
    },
    [isReadOnly, onNodesChange, debouncedUpdateRecipe]
  );

  const handleOnEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      if (isReadOnly) return;

      onEdgesChange(changes);
      emitRecipeModified();
      debouncedUpdateRecipe();
    },
    [isReadOnly, onEdgesChange, debouncedUpdateRecipe]
  );

  const handleOnConnect = useCallback(
    (params: Connection) => {
      if (isReadOnly) return;

      onConnectFlow(params);
      setEdges((eds) => addEdge({ ...params, animated: true, type: 'smoothstep' }, eds));
      emitRecipeModified();
      debouncedUpdateRecipe();
    },
    [isReadOnly, onConnectFlow, setEdges, debouncedUpdateRecipe]
  );

  return {
    handleOnNodesChange,
    handleOnEdgesChange,
    handleOnConnect,
  };
};
